import { LoginResponse } from "app/types/User";
import { getStoredAuth } from "utils/authStorage";
import { Task } from "app/user/tasks/Task";
import { action, makeAutoObservable } from "mobx";
import { io, Socket } from "socket.io-client";

const createDefaultLoginResponse = (): LoginResponse => ({
  success: false,
  message: "",
  user: [],
  accessToken: "",
  refreshToken: "",
  navigations: [],
  notes: [],
  dashboardCounts: {} as any,
  tasks: { onTime: [], critical: [] } as any,
  pendingTasks: new Map() as any,
  userSettings: [],
});

class AppStore {
  loginResponse: LoginResponse = createDefaultLoginResponse();
  selectedEntity: number = -1;
  dashboardViewUserId: number | null = null;
  showEntityDialog: boolean = false;
  adminModeEnabled: boolean = false;
  userEntities: Entity[] = [];
  loading: boolean = false;
  tasksData: Task[] = [];
  socket?: Socket;
  toastOpen: boolean = false;
  toastMessage: string = "";
  toastSeverity: "error" | "success" | "info" | "warning" = "success";

  constructor() {
    makeAutoObservable(this, {
      setSelectedEntity: action,
      setDashboardViewUserId: action,
      setShowEntityDialog: action,
      setAdminModeEnabled: action,
      setLoginResponse: action,
      setUserEntities: action,
      setToastOpen: action,
      setToastMessage: action,
      setToastSeverity: action,
      showToast: action,
      clearToast: action,
      clearSession: action,
    });
  }

  setToastOpen(open: boolean) {
    this.toastOpen = open;
  }

  setToastMessage(message: string) {
    this.toastMessage = message;
  }

  setToastSeverity(severity: "error" | "success" | "info" | "warning") {
    this.toastSeverity = severity;
  }

  showToast(
    message: string,
    severity: "error" | "success" | "info" | "warning"
  ) {
    this.setToastOpen(true);
    this.setToastMessage(message);
    this.setToastSeverity(severity);
  }

  clearToast() {
    this.setToastMessage("");
    this.setToastSeverity("info");
  }

  private getStoredAuthData(): LoginResponse | null {
    try {
      const stored = getStoredAuth();
      if (!stored) return null;
      return JSON.parse(stored) as LoginResponse;
    } catch (error) {
      console.warn("Unable to parse stored auth data", error);
      return null;
    }
  }

  async loadSocket(url?: string) {
    try {
      if (this.socket && this.socket.connected) {
        return this.socket;
      }
      const socketUrl =
        url ||
        process.env.REACT_APP_SOCKET_URL ||
        "http://localhost:5200";
      const authData =
        this.loginResponse?.accessToken && this.loginResponse?.user?.length
          ? this.loginResponse
          : this.getStoredAuthData();
      const token = authData?.accessToken;
      const userId = authData?.user?.[0]?.userId;
      if (!token || !userId) {
        return undefined;
      }

      const socketPath = process.env.REACT_APP_SOCKET_PATH;
      this.socket = io(socketUrl, {
        ...(socketPath ? { path: socketPath } : {}),
        transports: ["websocket", "polling"],
        withCredentials: true,
        auth: {
          ...(token ? { token } : {}),
          ...(userId ? { userId } : {}),
        },
      });

      this.socket.on("connect", () => {
        console.log("Socket connected:", this.socket?.id);
      });

      // Keep server-aware of this user's presence
      this.socket.on("connect", () => {
        this.socket?.emit("register", { userId });
      });

      this.socket.on("disconnect", (reason) => {
        console.log("Socket disconnected:", reason);
      });

      this.socket.on("connect_error", (err) => {
        console.error("Socket connect_error:", err?.message || err);
      });

      this.socket.on("error", (err) => {
        console.error("Socket error:", err);
      });

      const mgr = this.socket.io;
      mgr.on("reconnect_attempt", (attempt) =>
        console.log("Socket reconnect_attempt:", attempt)
      );
      mgr.on("reconnect_error", (err) =>
        console.error("Socket reconnect_error:", err?.message || err)
      );
      mgr.on("reconnect_failed", () =>
        console.error("Socket reconnect_failed")
      );

      console.log("Connecting to socket:", socketUrl);
      return this.socket;
    } catch (err) {
      console.error("Failed to initialize socket:", err);
      return undefined;
    }
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  setCurrentTasks(tasks: Task[]) {
    this.tasksData = tasks;
  }

  setSelectedEntity(entity: number) {
    this.selectedEntity = entity;
  }

  setDashboardViewUserId(userId: number | null) {
    this.dashboardViewUserId = userId;
  }

  setShowEntityDialog(showEntityDialog: boolean) {
    this.showEntityDialog = showEntityDialog;
  }

  setAdminModeEnabled(adminModeEnabled: boolean) {
    this.adminModeEnabled = adminModeEnabled;
  }

  setLoginResponse(response: LoginResponse) {
    this.loginResponse = response;
  }

  setUserEntities(entities: Entity[]) {
    this.userEntities = entities;
  }

  clearSession() {
    try {
      this.socket?.disconnect();
    } catch {}
    this.socket = undefined;
    this.loginResponse = createDefaultLoginResponse();
    this.selectedEntity = -1;
    this.dashboardViewUserId = null;
    this.showEntityDialog = false;
    this.adminModeEnabled = false;
    this.userEntities = [];
    this.tasksData = [];
  }
}

const appStore = new AppStore();

export default appStore;
